/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useCallback, useEffect, useRef, useState } from 'react';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import { useAuth } from '../context/AuthContext';

const FETCH_LIMIT = 100;
const MAX_PAGES = 50;

/**
 * Fetches service request types.
 *
 * Service request types are platform-level and only usable once assigned
 * (see docs/agent/modules/service-request-types.md) — this hook always
 * narrows to what the caller is actually allowed to use. Context is derived
 * from `user.accountUID`/`user.subAccountUID` rather than `user.scope`,
 * since a platform/account admin who has switched into an account or
 * sub-account context (via AccountSwitcher) keeps their original role scope
 * but gets those fields populated for the switched-into context — the same
 * narrowing must apply to them as to a "real" account/sub-account login:
 *   - A caller currently in a sub-account context (their own, or switched
 *     into) always resolves to that sub-account's assigned types
 *     (`subAccountUid` is ignored).
 *   - A caller currently in an account context (and not narrowed to one of
 *     its sub-accounts) resolves to that account's assigned types, unless
 *     `subAccountUid` narrows further to one of its sub-accounts.
 *   - A caller in no account/sub-account context (platform view) narrows to
 *     `subAccountUid` when given; otherwise:
 *     - `requireSubAccountUid: true` (default) — skip the request and return an
 *       empty list until a sub-account is chosen. Use this for forms where the
 *       type must be valid for one specific, about-to-be-selected sub-account
 *       (e.g. the New/Edit Service Request type picker).
 *     - `requireSubAccountUid: false` — fetch the full platform catalog so
 *       type names can be resolved for rows spanning multiple sub-accounts
 *       (e.g. the Service Requests list/detail pages).
 *
 * `includeInactive` (default false) — also return deactivated types. Needed
 * anywhere a service request's already-assigned type must still resolve to
 * its real name/labels after being deactivated (list grid, detail, edit
 * forms) — deactivating a type never rewrites the requests that reference
 * it, so those screens must keep looking it up. Leave false for pickers that
 * should only offer types open for new assignment (e.g. New Service Request).
 *
 * Also degrades to an empty list on fetch failure (e.g. a 403 for a caller
 * with neither service_requests.create nor service_requests.edit) rather
 * than throwing.
 */
const useServiceRequestTypes = (subAccountUid, options = {}) => {
  const { requireSubAccountUid = true, includeInactive = false } = options;
  const { user } = useAuth();
  const isSubAccountScope = !!user?.subAccountUID;
  const targetSubAccountUid = isSubAccountScope ? user?.subAccountUID : subAccountUid;
  const targetAccountUid = !targetSubAccountUid && user?.accountUID ? user.accountUID : undefined;
  const enabled = isSubAccountScope || !!targetAccountUid || !requireSubAccountUid || !!targetSubAccountUid;

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const requestIdRef = useRef(0);

  const fetchTypes = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!enabled) {
      setTypes([]);
      return;
    }
    try {
      setLoading(true);
      let page = 1;
      let all = [];
      for (;;) {
        const { data } = await serviceRequestTypeService.getServiceRequestTypes({
          limit: FETCH_LIMIT,
          page,
          includeInactive: includeInactive || undefined,
          ...(targetSubAccountUid ? { subAccountUid: targetSubAccountUid } : {}),
          ...(!targetSubAccountUid && targetAccountUid ? { accountUid: targetAccountUid } : {}),
        });
        if (requestId !== requestIdRef.current) return;
        const rows = data?.data || [];
        all = all.concat(rows);
        const total = data?.total ?? all.length;
        if (rows.length === 0 || all.length >= total || page >= MAX_PAGES) break;
        page += 1;
      }
      if (requestId !== requestIdRef.current) return;
      setTypes(all);
    } catch (error) {
      console.error('Failed to fetch service request types:', error);
      if (requestId === requestIdRef.current) setTypes([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, targetSubAccountUid, targetAccountUid, includeInactive]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  return { types, loading, refetch: fetchTypes };
};

export default useServiceRequestTypes;
